// SkillTreeViewer — T-016 Part B. Renders a Big 6 chain as ten connected
// nodes with the exact state styling from design system §5. The connector
// between two steps turns lime once the lower step is cleared. Each node
// is keyboard-activatable and opens StepDetailModal.

import { useMemo, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { canUnlockNext, type ProgressionStatus } from "../../utils/progressionEngine";
import type { Exercise, ExerciseProgress } from "../../types/calisthenics";
import { StepDetailModal } from "./StepDetailModal";

export type NodeState =
  | "locked"
  | "available"
  | "inProgress"
  | "beginner"
  | "intermediate"
  | "mastery";

function stateOf(
  exercise: Exercise,
  progress: Record<string, ExerciseProgress>,
  prev?: Exercise
): NodeState {
  const p = progress[exercise.id];
  if (!p) {
    // Step 1 is always available; later steps need mastery on the previous one.
    if (!prev) return "available";
    if (canUnlockNext(progress, exercise, prev)) {
      return "available";
    }
    return "locked";
  }
  // ADR-0013 D2: the per-exercise tier is now `bestTier` on the progress
  // row, computed only over completed workouts.
  const tier: ProgressionStatus = p.bestTier;
  if ((exercise.standard.holdSeconds ?? 0) > 0) {
    // Hold-based elite: mastery is `bestHoldSeconds >= holdSeconds`. Mirror
    // the engine's semantics in our viewer.
    if ((p.bestHoldSeconds ?? 0) >= (exercise.standard.holdSeconds ?? Infinity)) {
      return "mastery";
    }
    if ((p.bestHoldSeconds ?? 0) > 0) {
      return "inProgress";
    }
    return "available";
  }
  switch (tier) {
    case "mastery":
      return "mastery";
    case "intermediate":
      return "intermediate";
    case "beginner":
      return "beginner";
    case "none":
    default:
      return "inProgress";
  }
}

const STATE_CLASS: Record<NodeState, string> = {
  locked: "state-locked",
  available: "state-available",
  inProgress: "state-inProgress",
  beginner: "state-beginner",
  intermediate: "state-intermediate",
  mastery: "state-mastery",
};

export interface SkillTreeViewerProps {
  chain: readonly Exercise[];
  progress: Record<string, ExerciseProgress>;
  onStart?: (exercise: Exercise) => void;
}

export function SkillTreeViewer({
  chain,
  progress,
  onStart,
}: SkillTreeViewerProps): JSX.Element {
  const { t, locale } = useI18n();
  const [active, setActive] = useState<Exercise | null>(null);

  // Sort by stepNumber ascending just in case the chain is out of order.
  const sorted = [...chain].sort((a, b) => a.stepNumber - b.stepNumber);

  // Compute every node's state once; B8: a node is available iff the
  // previous node is at mastery.
  const states = useMemo(
    () =>
      sorted.map((exercise, idx) =>
        stateOf(exercise, progress, idx > 0 ? sorted[idx - 1] : undefined)
      ),
    [sorted, progress]
  );

  const activeState = active ? states[sorted.indexOf(active)] : null;

  return (
    <div data-testid="skill-tree" className="flex flex-col gap-0">
      <ol className="flex flex-col gap-0">
        {sorted.map((exercise, idx) => {
          const state = states[idx];
          const cleared =
            state === "beginner" || state === "intermediate" || state === "mastery";
          const prevMastery = idx === 0 || states[idx - 1] === "mastery";
          return (
            <li key={exercise.id} className="flex flex-col items-stretch">
              {/* Connector above each step except the first. */}
              {idx > 0 && (
                <div
                  aria-hidden="true"
                  data-testid="connector"
                  data-cleared={prevMastery}
                  className="mx-auto h-8 w-[2px]"
                  style={{
                    backgroundColor: prevMastery ? "var(--color-lime)" : "var(--color-line)",
                    transition: "background-color 200ms cubic-bezier(0.2, 0, 0, 1)",
                  }}
                />
              )}
              <Node
                exercise={exercise}
                state={state}
                onActivate={() => setActive(exercise)}
                locale={locale}
                t={t}
                cleared={cleared}
              />
            </li>
          );
        })}
      </ol>
      {active && activeState && (
        <StepDetailModal
          exercise={active}
          state={activeState}
          progress={progress[active.id]}
          onClose={() => setActive(null)}
          onStart={onStart}
        />
      )}
    </div>
  );
}

function Node({
  exercise,
  state,
  onActivate,
  locale,
  t,
  cleared,
}: {
  exercise: Exercise;
  state: NodeState;
  onActivate: () => void;
  locale: "de" | "hu" | "en";
  t: (k: string, p?: Record<string, string | number>) => string;
  cleared: boolean;
}): JSX.Element {
  const stateStyle: Record<NodeState, { fill: string; border: string; label: string; icon: string }> = {
    locked: {
      fill: "transparent",
      border: "1px solid var(--color-line)",
      label: t("treeState.locked"),
      icon: "🔒",
    },
    available: {
      fill: "rgba(198,255,61,0.06)",
      border: "2px dashed var(--color-lime)",
      label: t("treeState.available"),
      icon: "⚡",
    },
    inProgress: {
      fill: "rgba(198,255,61,0.15)",
      border: "2px solid var(--color-lime)",
      label: t("treeState.inProgress"),
      icon: "⏳",
    },
    beginner: {
      fill: "rgba(198,255,61,0.40)",
      border: "2px solid var(--color-lime)",
      label: t("treeState.beginner"),
      icon: "✓",
    },
    intermediate: {
      fill: "rgba(198,255,61,0.70)",
      border: "2px solid var(--color-lime)",
      label: t("treeState.intermediate"),
      icon: "✓✓",
    },
    mastery: {
      fill: "var(--color-lime)",
      border: "2px solid var(--color-gold)",
      label: t("treeState.mastery"),
      icon: "👑",
    },
  };
  const s = stateStyle[state];
  const isMastery = state === "mastery";
  const isAvailable = state === "available";
  const ariaLabel = `${t("common.step")} ${exercise.stepNumber}: ${exercise.name[locale]} — ${s.label}`;
  void cleared;
  return (
    <button
      type="button"
      role="treeitem"
      data-testid="tree-node"
      data-state={state}
      className={`group flex w-full items-center gap-4 border bg-[var(--color-slab)] p-4 text-left transition-all active:scale-[0.99] ${STATE_CLASS[state]}`}
      style={{
        minHeight: "88px",
        backgroundColor: s.fill === "transparent" ? "var(--color-slab)" : s.fill,
        border: s.border,
        borderRadius: "var(--radius-card)",
        boxShadow: isMastery
          ? "0 0 24px rgba(255,215,0,0.4)"
          : isAvailable
            ? "0 0 16px rgba(198,255,61,0.2)"
            : "none",
        color: state === "mastery" ? "var(--color-void)" : "var(--color-ink-hi)",
        opacity: state === "locked" ? 0.6 : 1,
      }}
      aria-label={ariaLabel}
      aria-current={isMastery ? "step" : undefined}
      onClick={onActivate}
    >
      <div
        className="flex items-center justify-center font-display text-2xl"
        style={{
          minWidth: "40px",
          height: "40px",
          borderRadius: "var(--radius-sharp)",
          backgroundColor:
            state === "mastery"
              ? "rgba(0,0,0,0.25)"
              : state === "locked"
                ? "var(--color-slab)"
                : "rgba(204,255,0,0.15)",
          color:
            state === "mastery"
              ? "var(--color-void)"
              : state === "locked"
                ? "var(--color-ink-mute)"
                : "var(--color-lime)",
          fontVariantNumeric: "tabular-nums",
        }}
        aria-hidden="true"
      >
        {exercise.stepNumber}
      </div>
      <span className="flex flex-1 flex-col gap-1">
        <span
          className="font-display uppercase tracking-[0.02em]"
          style={{
            fontSize: 20,
            lineHeight: 1.1,
            color:
              state === "beginner" || state === "intermediate" || state === "mastery"
                ? "var(--color-void)"
                : "var(--color-ink-hi)",
          }}
        >
          {exercise.name[locale]}
        </span>
        <span
          className="flex items-center gap-1.5 font-subhead text-xs uppercase tracking-[0.06em]"
          style={{
            color:
              state === "beginner" || state === "intermediate" || state === "mastery"
                ? "var(--color-void)"
                : state === "available"
                  ? "var(--color-lime)"
                  : state === "locked"
                    ? "var(--color-ink-mute)"
                    : "var(--color-ink-hi)",
          }}
        >
          <span aria-hidden="true">{s.icon}</span>
          <span>{s.label}</span>
        </span>
      </span>
    </button>
  );
}
